/**
 * 역할에 속한 구성원
 * ---------------------------------------------------------------------------
 * 역할을 고르면 **그 역할을 가진 사람들**을 보여 주고, 여러 명을 한 번에
 * 넣고 뺀다.
 *
 * ## 왜 이 화면이 필요한가
 * 지금까지 역할은 「구성원」 화면에서 **한 사람씩** 골라야 했다. 사람이 18명인데
 * 역할이 붙은 사람은 5명뿐인 상태를 알아채기도 어려웠다 — 역할 쪽에서 보면
 * 숫자만 보이고 누구인지는 안 보였기 때문이다.
 *
 * ## 한 사람이 여러 역할을 가져도 된다
 * 여기서 넣고 빼는 것은 **그 역할 하나**다. 다른 역할은 건드리지 않는다.
 * 권한은 가진 역할들의 **합집합**이라, 역할을 더 받으면 할 수 있는 일이
 * 늘기만 하고 줄지 않는다. "여러 역할 중 높은 쪽이 적용된다" 가 이 뜻이다.
 *
 * ## 자리(과금)
 * ECO·CRM 계열 역할은 그 제품의 **사용자 수**를 늘린다. 계약 인원을 넘기면
 * 서버가 금액을 담아 거절하고, 여기서 그 금액을 띄운 뒤 사유를 받아 다시
 * 보낸다. 구성원 등록과 같은 창을 쓴다.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Empty,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import {
  askSeatApproval,
  isSeatApprovalRequired,
  isSeatLimitExceeded,
  showSeatLimit,
} from '../users/seatOverage';

const { Text } = Typography;

interface DirectoryItem {
  userId: string;
  name: string;
  email: string;
  empNo: string | null;
  departmentName: string | null;
  status: string;
  roleNames: string[];
}

interface Paged {
  items: DirectoryItem[];
  total: number;
}

/** 한 번에 보여 주는 수. 역할 하나에 수백 명이 붙는 일은 드물다. */
const PAGE_SIZE = 200;

export default function RoleMembersPanel({
  roleId,
  roleName,
  canManage,
}: {
  roleId: string;
  roleName: string;
  canManage: boolean;
}) {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();

  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const members = useQuery<Paged, NormalizedError>({
    queryKey: ['roles', roleId, 'members'],
    queryFn: async () =>
      (
        await apiClient.get<Paged>('/users', {
          params: { roleId, size: PAGE_SIZE, page: 1, sort: 'name:asc' },
        })
      ).data,
  });

  const refresh = async () => {
    setSelected([]);
    await Promise.all([
      client.invalidateQueries({ queryKey: ['roles'] }),
      client.invalidateQueries({ queryKey: ['users'] }),
    ]);
  };

  const remove = async () => {
    if (selected.length === 0) return;
    setBusy(true);
    try {
      const { data } = await apiClient.delete<{ removed: number }>(
        `/roles/${roleId}/members`,
        { data: { userIds: selected } },
      );
      message.success(`${data.removed}명을 「${roleName}」에서 뺐습니다.`);
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = () => {
    Modal.confirm({
      title: `${selected.length}명을 「${roleName}」에서 뺄까요?`,
      content:
        '이 역할이 주던 권한만 사라집니다. 다른 역할은 그대로입니다. ' +
        '역할이 하나도 남지 않는 사람에게는 「일반 사용자」가 들어갑니다.',
      okText: '빼기',
      okButtonProps: { danger: true },
      cancelText: '취소',
      onOk: () => void remove(),
    });
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {members.isError && <Alert type="error" showIcon message={errorText(members.error)} />}

      <Space>
        {canManage && (
          <Button
            size="small"
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setAdding(true)}
          >
            구성원 추가
          </Button>
        )}
        {canManage && (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={selected.length === 0}
            loading={busy}
            onClick={confirmRemove}
          >
            선택 빼기 ({selected.length})
          </Button>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          이 역할을 가진 구성원 {members.data?.total ?? 0}명
        </Text>
      </Space>

      <Table<DirectoryItem>
        rowKey="userId"
        size="small"
        loading={members.isLoading}
        dataSource={members.data?.items ?? []}
        pagination={false}
        scroll={{ y: 360 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="이 역할을 가진 구성원이 없습니다"
            />
          ),
        }}
        rowSelection={
          canManage
            ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }
            : undefined
        }
        columns={[
          { title: '이름', dataIndex: 'name', width: 120 },
          { title: '사번', dataIndex: 'empNo', width: 100, render: (v) => v ?? '-' },
          { title: '부서', dataIndex: 'departmentName', width: 160, render: (v) => v ?? '-' },
          { title: '메일', dataIndex: 'email', ellipsis: true },
          {
            title: '가진 역할',
            dataIndex: 'roleNames',
            width: 260,
            render: (names: string[]) => (
              <Space size={4} wrap>
                {names.map((n) => (
                  <Tag key={n} color={n === roleName ? 'blue' : undefined}>
                    {n}
                  </Tag>
                ))}
              </Space>
            ),
          },
        ]}
      />

      <AddMembersModal
        open={adding}
        roleId={roleId}
        roleName={roleName}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          void refresh();
        }}
      />
    </Space>
  );
}

/**
 * 추가할 사람 고르기.
 *
 * 이 역할을 **아직 안 가진** 사람만 보여 준다(`excludeRole`). 이미 가진 사람이
 * 목록에 섞여 있으면 몇 명이 실제로 늘어나는지 세기 어렵고, 자리 계산도
 * 그만큼 헷갈린다.
 */
function AddMembersModal({
  open,
  roleId,
  roleName,
  onClose,
  onDone,
}: {
  open: boolean;
  roleId: string;
  roleName: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = AntdApp.useApp();
  const [keyword, setKeyword] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const candidates = useQuery<Paged, NormalizedError>({
    queryKey: ['roles', roleId, 'candidates', keyword],
    enabled: open,
    queryFn: async () =>
      (
        await apiClient.get<Paged>('/users', {
          params: {
            roleId,
            excludeRole: true,
            keyword: keyword || undefined,
            size: PAGE_SIZE,
            page: 1,
            sort: 'name:asc',
          },
        })
      ).data,
  });

  const submit = async (reason?: string) => {
    setSaving(true);
    try {
      const { data } = await apiClient.post<{ added: number; skipped: number }>(
        `/roles/${roleId}/members`,
        { userIds: picked, ...(reason ? { seatOverageReason: reason } : {}) },
      );
      message.success(`${data.added}명에게 「${roleName}」을 주었습니다.`);
      setPicked([]);
      setKeyword('');
      onDone();
    } catch (err) {
      const error = err as NormalizedError;

      if (isSeatLimitExceeded(error)) {
        showSeatLimit(error);
        return;
      }

      if (isSeatApprovalRequired(error)) {
        const agreed = await askSeatApproval(error, picked.length);
        if (agreed) {
          // 사유를 담아 한 번 더. 서버가 계약 수량을 올리고 근거를 남긴다.
          await submit(agreed);
        }
        return;
      }

      message.error(errorText(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={`「${roleName}」에 추가할 구성원`}
      width={760}
      okText={`추가 (${picked.length}명)`}
      cancelText="취소"
      okButtonProps={{ disabled: picked.length === 0 }}
      confirmLoading={saving}
      onCancel={() => {
        setPicked([]);
        setKeyword('');
        onClose();
      }}
      onOk={() => void submit()}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Input.Search
          allowClear
          placeholder="이름으로 찾기 (초성도 됩니다)"
          onSearch={setKeyword}
          style={{ maxWidth: 320 }}
        />

        <Table<DirectoryItem>
          rowKey="userId"
          size="small"
          loading={candidates.isLoading}
          dataSource={candidates.data?.items ?? []}
          pagination={false}
          scroll={{ y: 320 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="추가할 수 있는 구성원이 없습니다"
              />
            ),
          }}
          rowSelection={{
            selectedRowKeys: picked,
            onChange: (keys) => setPicked(keys as string[]),
          }}
          columns={[
            { title: '이름', dataIndex: 'name', width: 120 },
            { title: '부서', dataIndex: 'departmentName', width: 150, render: (v) => v ?? '-' },
            { title: '메일', dataIndex: 'email', ellipsis: true },
            {
              title: '지금 가진 역할',
              dataIndex: 'roleNames',
              width: 220,
              render: (names: string[]) =>
                names.length === 0 ? (
                  <Text type="secondary">(없음)</Text>
                ) : (
                  <Space size={4} wrap>
                    {names.map((n) => (
                      <Tag key={n}>{n}</Tag>
                    ))}
                  </Space>
                ),
            },
          ]}
        />

        <Text type="secondary" style={{ fontSize: 12 }}>
          이미 가진 역할은 그대로 둡니다. 역할을 더 받으면 할 수 있는 일이 늘어날 뿐,
          줄지 않습니다.
        </Text>
      </Space>
    </Modal>
  );
}
